import { getApolloClient } from '@faustwp/core';
import { CaseStudyPage } from '../../../components/CaseStudyPage';
import {
  GET_POST_BY_DATABASE_ID,
  GET_POST_BY_DATABASE_ID_MINIMAL,
  GET_POST_BY_SLUG,
  GET_POST_BY_SLUG_AND_CATEGORY,
  GET_POST_BY_SLUG_AND_CATEGORY_MINIMAL,
  GET_POST_BY_SLUG_AND_LANGUAGE,
  GET_POST_BY_SLUG_MINIMAL,
  GET_POST_BY_SLUG_TRANSLATIONS_MINIMAL,
  GET_POST_TRANSLATIONS_BY_DATABASE_ID,
} from '../../../lib/queries/caseStudyBySlug';
import { ENGLISH_CATEGORY_SLUG, FINNISH_CATEGORY_SLUG } from '../../../lib/caseStudyPaths';

function getEnglishTranslation(post) {
  const translations = post?.translations ?? [];
  const code = (c) => (c ?? '').toLowerCase();
  return translations.find((t) => code(t?.language?.code).startsWith('en')) ?? null;
}

/** Pick the Finnish translation (Polylang). language.code can be 'fi', 'fi_FI', etc. */
function getFinnishTranslation(post) {
  const translations = post?.translations ?? [];
  const code = (c) => (c ?? '').toLowerCase();
  return translations.find((t) => code(t?.language?.code).startsWith('fi')) ?? null;
}

export default function EnWorkCaseStudyPage({ post, generalSettings, fiSlug }) {
  if (!post) return null;
  return (
    <CaseStudyPage
      post={post}
      generalSettings={generalSettings}
      lang="en"
      fiSlug={fiSlug}
    />
  );
}

export async function getStaticProps(ctx) {
  const { slug } = ctx.params ?? {};
  if (!slug) {
    return { notFound: true };
  }
  const client = getApolloClient();
  let data = null;
  let post = null;

  // 1) Try Polylang language filter: fetch English post by slug + language EN (full ACF on that post)
  if (!post) {
    try {
      const res = await client.query({
        query: GET_POST_BY_SLUG_AND_LANGUAGE,
        variables: { slug },
      });
      data = res?.data;
      post = data?.posts?.nodes?.[0] ?? null;
    } catch {
      // Schema may not have language filter (WPGraphQL Polylang not active or different type)
    }
  }

  // 2) Polylang: fetch post by slug. If the node is already EN, fetch full post by its databaseId.
  //    If the node is FI (or other), get EN translation's databaseId and fetch full EN post by ID.
  if (!post) {
    try {
      const res = await client.query({
        query: GET_POST_BY_SLUG_TRANSLATIONS_MINIMAL,
        variables: { slug },
      });
      data = res?.data;
      const node = data?.posts?.nodes?.[0] ?? null;
      const nodeLang = (node?.language?.code ?? '').toLowerCase();
      const nodeIsEnglish = nodeLang.startsWith('en');
      const enTranslation = node ? getEnglishTranslation(node) : null;
      const idToFetch = nodeIsEnglish ? node?.databaseId : enTranslation?.databaseId;
      if (idToFetch) {
        try {
          const fullRes = await client.query({
            query: GET_POST_BY_DATABASE_ID,
            variables: { databaseId: String(idToFetch) },
          });
          const fullData = fullRes?.data;
          if (fullData?.post) {
            post = fullData.post;
            data = fullData;
          }
        } catch {
          const minimalRes = await client.query({
            query: GET_POST_BY_DATABASE_ID_MINIMAL,
            variables: { databaseId: String(idToFetch) },
          });
          const minimalData = minimalRes?.data;
          if (minimalData?.post) {
            post = minimalData.post;
            data = minimalData;
          }
        }
      }
      if (!post && node) {
        post = enTranslation ?? node;
      }
    } catch {
      // Schema may not have language/translations (WPGraphQL Polylang not active)
    }
  }

  // 3) Fallback: English category, then Finnish category, then slug only
  if (!post) {
    try {
      let res = await client.query({
        query: GET_POST_BY_SLUG_AND_CATEGORY,
        variables: { slug, categorySlug: ENGLISH_CATEGORY_SLUG },
      });
      data = res?.data;
      post = data?.posts?.nodes?.[0] ?? null;
      if (!post) {
        res = await client.query({
          query: GET_POST_BY_SLUG_AND_CATEGORY,
          variables: { slug, categorySlug: FINNISH_CATEGORY_SLUG },
        });
        data = res?.data;
        post = data?.posts?.nodes?.[0] ?? null;
      }
      if (!post) {
        res = await client.query({
          query: GET_POST_BY_SLUG,
          variables: { slug },
        });
        data = res?.data;
        post = data?.posts?.nodes?.[0] ?? null;
      }
    } catch {
      try {
        let res = await client.query({
          query: GET_POST_BY_SLUG_AND_CATEGORY_MINIMAL,
          variables: { slug, categorySlug: ENGLISH_CATEGORY_SLUG },
        });
        data = res?.data;
        post = data?.posts?.nodes?.[0] ?? null;
        if (!post) {
          res = await client.query({
            query: GET_POST_BY_SLUG_AND_CATEGORY_MINIMAL,
            variables: { slug, categorySlug: FINNISH_CATEGORY_SLUG },
          });
          data = res?.data;
          post = data?.posts?.nodes?.[0] ?? null;
        }
        if (!post) {
          res = await client.query({
            query: GET_POST_BY_SLUG_MINIMAL,
            variables: { slug },
          });
          data = res?.data;
          post = data?.posts?.nodes?.[0] ?? null;
        }
      } catch {
        // Keep post null; will return notFound
      }
    }
  }

  const generalSettings = data?.generalSettings ?? null;
  if (!post) {
    return { notFound: true };
  }

  // Get Finnish translation slug for FI link (EN page may have different slug than FI)
  let fiSlug = null;
  try {
    const trRes = await client.query({
      query: GET_POST_TRANSLATIONS_BY_DATABASE_ID,
      variables: { databaseId: String(post.databaseId) },
    });
    const trNode = trRes?.data?.post ?? null;
    const fiTranslation = trNode ? getFinnishTranslation(trNode) : null;
    fiSlug = fiTranslation?.slug ?? null;
  } catch {
    // WPGraphQL Polylang not active or no translations
  }

  return {
    props: { post, generalSettings, fiSlug: fiSlug ?? null },
    revalidate: 60,
  };
}

export async function getStaticPaths() {
  return { paths: [], fallback: 'blocking' };
}
